import { getContrast } from "color2k"

/**
 * Creates a color contrast validator for Element UI forms
 * @param {Object} options - Configuration options
 * @param {string} options.backgroundColor - Background color to test against (default: "#FFFFFF")
 * @param {number} options.minContrastRatio - Minimum required contrast ratio (default: 2)
 * @param {Object} options.messages - Custom error messages
 * @param {string} options.messages.required - Message when no color is provided
 * @param {string} options.messages.lowContrast - Message when contrast is too low (receives contrastRatio and minContrastRatio)
 * @param {string} options.messages.invalidColor - Message when color format is invalid
 * @returns {Function} Element UI validator function
 */
export function createColorContrastValidator(options = {}) {
  const {
    backgroundColor = "#FFFFFF",
    minContrastRatio = 2.0,
    messages = {}
  } = options

  const defaultMessages = {
    required: "Bitte wählen Sie eine Farbe aus",
    lowContrast: (contrastRatio, minRatio) =>
      `Ausgewählte Farbe zu hell (Kontrast zu gering: ${contrastRatio.toFixed(
        2
      )}:1). Mindestens ${minRatio}:1 erforderlich für gute Lesbarkeit.`,
    invalidColor: "Ungültige Farbe"
  }

  const finalMessages = { ...defaultMessages, ...messages }

  return function validateColorContrast(rule, value, callback) {
    if (!value) {
      callback(new Error(finalMessages.required))
      return
    }

    try {
      const contrastRatio = getContrast(value, backgroundColor)

      if (contrastRatio < minContrastRatio) {
        const errorMessage =
          typeof finalMessages.lowContrast === "function"
            ? finalMessages.lowContrast(contrastRatio, minContrastRatio)
            : finalMessages.lowContrast

        callback(new Error(errorMessage))
      } else {
        callback()
      }
    } catch (error) {
      callback(new Error(finalMessages.invalidColor))
    }
  }
}

/**
 * Default color contrast validator
 * Ready to use for most common cases
 */
export const defaultColorContrastValidator = createColorContrastValidator()

/**
 * WCAG AA compliant validator for normal text (4.5:1 ratio)
 */
export const wcagAAValidator = createColorContrastValidator({
  minContrastRatio: 4.5
})

/**
 * WCAG AAA compliant validator for normal text (7:1 ratio)
 */
export const wcagAAAValidator = createColorContrastValidator({
  minContrastRatio: 7
})
